import type { FC, ReactNode } from 'react'
import type { Category, Product } from '@/types'

import Link from 'next/link'
import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { useReveal } from '@/hooks/use-reveal'
import { buildCustomRequestMessage } from '@/lib/whatsapp'
import { useSettings } from '@/components/providers/settings-provider'
import { ProductCard } from '@/components/shop/product-card'
import { ProductImage } from '@/components/shop/product-image'
import { WhatsAppLink } from '@/components/shop/whatsapp-link'

import {
    ArrowRight,
    ArrowUpRight,
    Gift,
    HandHeart,
    MessageCircleHeart,
    MousePointerClick,
    Palette,
    PackageCheck,
    ShoppingBag,
    Sparkles
} from 'lucide-react'

interface SectionHeadingProps {
    eyebrow: string
    title: ReactNode
    description?: ReactNode
    align?: 'left' | 'center'
    tone?: 'light' | 'dark'
    action?: ReactNode
}

export const SectionHeading: FC<SectionHeadingProps> = ({ eyebrow, title, description, align = 'left', tone = 'light', action }): ReactNode => {
    const dark = tone === 'dark'

    return (
        <div className={cn('flex flex-col gap-6 md:flex-row md:items-end md:justify-between', align === 'center' && 'items-center text-center md:flex-col md:items-center')}>
            <div className={cn('max-w-2xl', align === 'center' && 'mx-auto')}>
                <p data-reveal className={cn('text-xs font-bold uppercase tracking-[0.26em]', dark ? 'text-blush-200' : 'text-blush-600')}>
                    {eyebrow}
                </p>
                <h2 data-reveal className={cn('mt-4 font-heading text-4xl leading-[1.05] tracking-[-0.015em] font-soft sm:text-5xl', dark ? 'text-cream-50' : 'text-cocoa-900')}>
                    {title}
                </h2>
                {description && (
                    <p data-reveal className={cn('mt-5 text-base leading-relaxed sm:text-lg', dark ? 'text-cream-200/80' : 'text-muted-foreground')}>
                        {description}
                    </p>
                )}
            </div>
            {action && <div data-reveal className='shrink-0'>{action}</div>}
        </div>
    )
}

const CARD_TONES = ['bg-blush-100', 'bg-peach-100', 'bg-sand-100', 'bg-blush-50', 'bg-cream-200', 'bg-peach-50', 'bg-cream-100']

// ---------------------------------------------------------------------------
// Collections
// ---------------------------------------------------------------------------

interface CollectionsSectionProps {
    categories: Array<Category>
    products: Array<Product>
}

export const CollectionsSection: FC<CollectionsSectionProps> = ({ categories, products }): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useReveal(root)

    if (!categories.length) return null

    return (
        <section ref={root} id='collections' className='relative scroll-mt-24 bg-cream-50 py-24 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    eyebrow='Koleksi'
                    title={<>Dunia mungil, <span className='italic text-blush-600'>dirajut dengan tangan</span></>}
                    description='Dari teman peluk klasik sampai boneka custom satu-satunya, setiap koleksi dirajut dalam palet lembut khas kami: krem, merah muda, peach, dan cokelat rajut.'
                    action={(
                        <Link href='/shop' className='group inline-flex items-center gap-2 rounded-full border border-cocoa-700/15 bg-white px-5 py-3 text-sm font-semibold text-cocoa-800 transition hover:border-cocoa-700/30'>
                            Jelajahi Koleksi
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                        </Link>
                    )}
                />

                <div className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                    {categories.map((category, index) => {
                        const lead = products.find(product => product.category?.slug === category.slug && product.image)
                            ?? products.find(product => product.category?.slug === category.slug && product.isFeatured)
                            ?? products.find(product => product.category?.slug === category.slug)
                        const picture = category.image ?? lead?.image ?? null

                        return (
                            <Link
                                key={category.id}
                                href={`/shop?category=${category.slug}`}
                                data-reveal
                                className={cn(
                                    'group relative flex min-h-[340px] flex-col overflow-hidden rounded-[32px] p-7 transition-transform duration-500 hover:-translate-y-1',
                                    CARD_TONES[index % CARD_TONES.length]
                                )}
                            >
                                <div className='relative z-10 max-w-[62%]'>
                                    <p className='text-[11px] font-bold uppercase tracking-[0.2em] text-cocoa-500'>
                                        <span>{category.productCount}</span>
                                        <span> produk</span>
                                    </p>
                                    <h3 className='mt-2 font-heading text-[1.7rem] leading-tight text-cocoa-900'>
                                        <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                            {category.name}
                                        </span>
                                    </h3>
                                    {category.description && (
                                        <p className='mt-3 line-clamp-4 text-sm leading-relaxed text-cocoa-700/85'>
                                            <span data-mdlfy-cms={`categories:${category.id}:description`}>
                                                {category.description}
                                            </span>
                                        </p>
                                    )}
                                </div>

                                {(picture || lead?.designKey) && (
                                    <ProductImage
                                        src={picture}
                                        alt={category.imageAlt ?? `Koleksi ${category.name}`}
                                        designKey={lead?.designKey ?? null}
                                        mainColor={lead?.colors[0]?.hex ?? null}
                                        accentColor={lead?.accentColor ?? null}
                                        backdrop='none'
                                        className={cn(
                                            'pointer-events-none absolute -bottom-4 -right-6 aspect-square w-[62%] transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-2 group-hover:scale-105',
                                            picture && 'bottom-0 right-0 w-[48%] overflow-hidden rounded-tl-[28px]'
                                        )}
                                    />
                                )}

                                <span className='relative z-10 mt-auto inline-flex size-11 items-center justify-center rounded-full bg-white/80 text-cocoa-800 shadow-sm transition group-hover:bg-cocoa-800 group-hover:text-cream-50'>
                                    <ArrowUpRight className='size-5' />
                                </span>
                            </Link>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

// ---------------------------------------------------------------------------
// New arrivals
// ---------------------------------------------------------------------------

export const NewArrivalsSection: FC<{ products: Array<Product> }> = ({ products }): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useReveal(root)

    if (!products.length) return null

    return (
        <section ref={root} id='terbaru' className='scroll-mt-24 bg-cream-50 pb-24 sm:pb-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    eyebrow='Produk Terbaru'
                    title={<>Baru turun <span className='italic text-blush-600'>dari jarum rajut</span></>}
                    description='Boneka dan charm terbaru yang baru saja selesai dirajut dan siap menemanimu.'
                    action={(
                        <Link href='/shop?sort=newest' className='group inline-flex items-center gap-2 text-sm font-semibold text-cocoa-800 underline-offset-4 hover:underline'>
                            Lihat semua produk terbaru
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                        </Link>
                    )}
                />

                <div className='no-scrollbar -mx-5 mt-14 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 sm:overflow-visible sm:px-0 lg:grid-cols-4'>
                    {products.slice(0, 8).map(product => (
                        <div key={product.id} data-reveal className='w-[70%] shrink-0 snap-start sm:w-auto'>
                            <ProductCard product={product} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

// ---------------------------------------------------------------------------
// Bestsellers
// ---------------------------------------------------------------------------

export const FavouritesSection: FC<{ products: Array<Product> }> = ({ products }): ReactNode => {
    const root = useRef<HTMLElement>(null)
    const featured = products.filter(product => product.isFeatured).slice(0, 8)
    const list = featured.length >= 4 ? featured : products.slice(0, 8)

    useReveal(root)

    if (!list.length) return null

    return (
        <section ref={root} id='terlaris' className='scroll-mt-24 bg-[linear-gradient(180deg,#fffaf3_0%,#f9ece2_100%)] py-24 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    eyebrow='Produk Terlaris'
                    title={<>Yang paling sering <span className='italic text-blush-600'>bikin jatuh hati</span></>}
                    description='Sahabat rajut yang paling banyak dipesan, siap duduk manis di rak, ikut di tas, atau dibungkus sebagai kado.'
                    action={(
                        <Link href='/shop?label=terlaris' className='group inline-flex items-center gap-2 text-sm font-semibold text-cocoa-800 underline-offset-4 hover:underline'>
                            Lihat semua produk terlaris
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                        </Link>
                    )}
                />

                <div className='mt-14 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-4'>
                    {list.map(product => (
                        <div key={product.id} data-reveal>
                            <ProductCard product={product} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

// ---------------------------------------------------------------------------
// Story
// ---------------------------------------------------------------------------

const STEPS = [
    {
        icon: Palette,
        title: 'Memilih benang',
        text: 'Benang katun susu yang lembut dalam palet krem, merah muda, peach, dan cokelat.'
    },
    {
        icon: Sparkles,
        title: 'Merajut dengan tangan',
        text: 'Setiap kepala, telinga, dan kaki dirajut putaran demi putaran dengan satu jarum rajut.'
    },
    {
        icon: HandHeart,
        title: 'Mengisi dan menjahit',
        text: 'Setiap bagian diisi, dibentuk, lalu dijahit menjadi satu agar duduknya pas.'
    },
    {
        icon: Gift,
        title: 'Dibungkus penuh cinta',
        text: 'Boneka yang sudah jadi dicek ulang, diberi label, dan dibungkus rapi sebelum dikirim.'
    }
]

export const StorySection: FC<{ product: Product | null }> = ({ product }): ReactNode => {
    const root = useRef<HTMLElement>(null)
    const settings = useSettings()

    useReveal(root)

    return (
        <section ref={root} id='story' className='relative scroll-mt-24 overflow-hidden bg-cream-50 py-24 sm:py-32'>
            <div className='mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2'>
                <div data-reveal className='relative mx-auto w-full max-w-lg'>
                    <div className='absolute inset-[6%] rounded-full bg-blush-100' />
                    <svg viewBox='0 0 400 400' aria-hidden='true' className='absolute inset-0 h-full w-full'>
                        <circle cx='200' cy='200' r='186' fill='none' stroke='#e8a5b0' strokeWidth='2' strokeDasharray='6 10' />
                        <path d='M40 300 C 90 380, 220 400, 300 350' fill='none' stroke='#d9a37f' strokeWidth='3' strokeLinecap='round' strokeDasharray='2 9' />
                    </svg>
                    {product && (
                        <ProductImage
                            src={product.image}
                            alt={product.imageAlt ?? `${product.name}, boneka rajut handmade`}
                            designKey={product.designKey}
                            mainColor={product.colors[0]?.hex ?? null}
                            accentColor={product.accentColor}
                            backdrop='none'
                            className={cn('relative aspect-square w-full', product.image && 'overflow-hidden rounded-full')}
                        />
                    )}
                    <div className='absolute -bottom-2 left-2 rounded-3xl bg-white px-5 py-4 shadow-[0_24px_50px_-30px_rgba(58,40,30,0.6)] sm:left-0'>
                        <p className='font-heading text-3xl text-cocoa-900'>
                            100%
                        </p>
                        <p className='text-xs font-semibold text-cocoa-600'>
                            dirajut dengan tangan
                        </p>
                    </div>
                </div>

                <div>
                    <SectionHeading
                        eyebrow='Cerita kami'
                        title={<>Little charms, <span className='italic text-blush-600'>big love</span></>}
                        description={settings.description
                            ? (settings.id ? <span data-mdlfy-cms={`store_settings:${settings.id}:description`}>{settings.description}</span> : settings.description)
                            : 'Setiap boneka berawal dari seutas benang dan sebuah jarum rajut. Putaran demi putaran ia terbentuk di tangan kami, lalu diberi detail sulaman kecil, sehingga tidak ada dua boneka yang benar-benar sama.'}
                    />

                    <ol className='mt-10 grid gap-4 sm:grid-cols-2'>
                        {STEPS.map((step, index) => {
                            const Icon = step.icon

                            return (
                                <li key={step.title} data-reveal className='rounded-[28px] border border-border bg-white/70 p-6'>
                                    <div className='flex items-center gap-3'>
                                        <span className='grid size-11 place-items-center rounded-2xl bg-blush-100 text-blush-600'>
                                            <Icon className='size-5' />
                                        </span>
                                        <span className='font-heading text-sm italic text-cocoa-500'>
                                            Langkah <span>{index + 1}</span>
                                        </span>
                                    </div>
                                    <h3 className='mt-4 font-heading text-xl text-cocoa-900'>
                                        {step.title}
                                    </h3>
                                    <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                        {step.text}
                                    </p>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </div>
        </section>
    )
}

// ---------------------------------------------------------------------------
// Limited edition
// ---------------------------------------------------------------------------

export const LimitedSection: FC<{ products: Array<Product>, category: Category | null }> = ({ products, category }): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useReveal(root)

    if (!products.length) return null

    return (
        <section ref={root} id='limited' className='relative scroll-mt-24 overflow-hidden bg-cocoa-900 py-24 text-cream-50 sm:py-28'>
            <div aria-hidden='true' className='pointer-events-none absolute -right-40 -top-40 size-[520px] rounded-full bg-blush-500/15 blur-3xl' />
            <div aria-hidden='true' className='pointer-events-none absolute -bottom-48 -left-32 size-[480px] rounded-full bg-caramel-400/15 blur-3xl' />

            <div className='relative mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    tone='dark'
                    eyebrow='Edisi Terbatas'
                    title={<>Dibuat sedikit saja, <span className='italic text-blush-200'>cepat habisnya</span></>}
                    description={category?.description
                        ? <span data-mdlfy-cms={`categories:${category.id}:description`}>{category.description}</span>
                        : 'Karakter fantasi yang hanya kami rajut beberapa buah setiap kali.'}
                    action={(
                        <Link
                            href='/shop?label=edisi-terbatas'
                            className='group inline-flex items-center gap-2 rounded-full bg-cream-50 px-5 py-3 text-sm font-semibold text-cocoa-900 transition hover:bg-white'
                        >
                            Lihat Edisi Terbatas
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                        </Link>
                    )}
                />

                <div className='mt-14 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3'>
                    {products.slice(0, 3).map(product => (
                        <div key={product.id} data-reveal>
                            <ProductCard product={product} tone='dark' />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

// ---------------------------------------------------------------------------
// Gallery strip
// ---------------------------------------------------------------------------

export const GallerySection: FC<{ products: Array<Product> }> = ({ products }): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useReveal(root)

    const row = products.filter(product => product.image || product.designKey).slice(0, 14)

    if (row.length < 4) return null

    const renderTile = (product: Product, index: number, hidden: boolean) => (
        <li key={`${product.id}-${hidden ? 'copy' : 'main'}`} aria-hidden={hidden || undefined} className='shrink-0'>
            <Link
                href={`/products/${product.slug}`}
                tabIndex={hidden ? -1 : undefined}
                className={cn(
                    'group block w-[220px] overflow-hidden rounded-[28px] bg-white shadow-[0_18px_40px_-28px_rgba(58,40,30,0.55)] sm:w-[260px]',
                    index % 2 === 1 && 'mt-10'
                )}
            >
                <ProductImage
                    src={product.image}
                    alt={hidden ? '' : product.imageAlt ?? product.name}
                    designKey={product.designKey}
                    mainColor={product.colors[0]?.hex ?? null}
                    accentColor={product.accentColor}
                    className='aspect-[4/5] w-full'
                    imageClassName='transition-transform duration-700 group-hover:scale-105'
                />
                <span className='flex items-center justify-between gap-2 px-4 py-3'>
                    <span className='truncate font-heading text-base text-cocoa-900'>
                        <span data-mdlfy-cms={`products:${product.id}:name`}>
                            {product.name}
                        </span>
                    </span>
                    <ArrowUpRight className='size-4 shrink-0 text-cocoa-500 transition group-hover:text-blush-600' />
                </span>
            </Link>
        </li>
    )

    return (
        <section ref={root} id='gallery' aria-label='Galeri produk' className='scroll-mt-24 overflow-hidden bg-[linear-gradient(180deg,#f9ece2_0%,#fffaf3_100%)] py-24 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    align='center'
                    eyebrow='Galeri'
                    title={<>Koleksi kami, <span className='italic text-blush-600'>dari dekat</span></>}
                    description='Arahkan kursor untuk berhenti sejenak, ketuk boneka mana pun untuk berkenalan.'
                />
            </div>

            <div data-reveal className='group/marquee relative mt-14 motion-reduce:overflow-x-auto'>
                <div aria-hidden='true' className='pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-[#fcf3ec] to-transparent sm:w-32' />
                <div aria-hidden='true' className='pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-[#fcf3ec] to-transparent sm:w-32' />
                <ul className='flex w-max gap-5 px-5 pb-12 motion-safe:animate-marquee group-hover/marquee:[animation-play-state:paused] group-focus-within/marquee:[animation-play-state:paused]'>
                    {row.map((product, index) => renderTile(product, index, false))}
                    {row.map((product, index) => renderTile(product, index, true))}
                </ul>
            </div>
        </section>
    )
}

// ---------------------------------------------------------------------------
// Custom orders
// ---------------------------------------------------------------------------

export const CustomSection: FC<{ product: Product | null, category: Category | null }> = ({ product, category }): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useReveal(root)

    return (
        <section ref={root} id='custom' className='scroll-mt-24 bg-cream-50 py-24 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='relative overflow-hidden rounded-[40px] bg-blush-100 p-8 sm:p-12 lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-12 lg:p-16'>
                    <div aria-hidden='true' className='pointer-events-none absolute -left-24 -top-24 size-72 rounded-full border-2 border-dashed border-blush-300/70' />
                    <div className='relative'>
                        <p data-reveal className='text-xs font-bold uppercase tracking-[0.26em] text-blush-600'>
                            Boneka Custom
                        </p>
                        <h2 data-reveal className='mt-4 font-heading text-4xl leading-[1.05] text-cocoa-900 font-soft sm:text-5xl'>
                            Kamu yang bermimpi, <span className='italic text-blush-600'>kami yang merajut</span>
                        </h2>
                        <p data-reveal className='mt-5 max-w-xl text-base leading-relaxed text-cocoa-700/85 sm:text-lg'>
                            Wujudkan orang tersayang, hewan peliharaan, atau karakter favorit menjadi boneka satu-satunya. Ceritakan idemu lewat WhatsApp dan kita rencanakan warna, ukuran, serta detail kecilnya bersama.
                        </p>
                        <ul data-reveal className='mt-8 space-y-3 text-sm font-semibold text-cocoa-800'>
                            {['Kirim foto atau ceritakan idemu', 'Pilih warna, ukuran, dan bajunya', 'Kami rajut dengan tangan, khusus untukmu'].map(item => (
                                <li key={item} className='flex items-center gap-3'>
                                    <span className='grid size-7 place-items-center rounded-full bg-white text-blush-600'>
                                        <HandHeart className='size-4' />
                                    </span>
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <div data-reveal className='mt-9 flex flex-wrap gap-3'>
                            <WhatsAppLink message={buildCustomRequestMessage} size='lg'>
                                Pesan Boneka Custom
                            </WhatsAppLink>
                            <Link
                                href={category ? `/shop?category=${category.slug}` : '/shop?label=custom'}
                                className='inline-flex h-14 items-center gap-2 rounded-full border border-cocoa-700/15 bg-white/70 px-6 text-base font-semibold text-cocoa-800 transition hover:bg-white'
                            >
                                Lihat contoh boneka custom
                            </Link>
                        </div>
                    </div>

                    {product && (product.image || product.designKey) && (
                        <div data-reveal className='relative mx-auto mt-12 w-full max-w-sm lg:mt-0'>
                            <div className='absolute inset-[8%] rounded-full bg-cream-50/70' />
                            <ProductImage
                                src={product.image}
                                alt={product.imageAlt ?? `${product.name}, boneka rajut custom`}
                                designKey={product.designKey}
                                mainColor={product.colors[0]?.hex ?? null}
                                accentColor={product.accentColor}
                                backdrop='none'
                                className={cn('relative aspect-square w-full', product.image && 'overflow-hidden rounded-full')}
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

// ---------------------------------------------------------------------------
// How to order
// ---------------------------------------------------------------------------

const ORDER_STEPS = [
    { icon: MousePointerClick, title: 'Pilih bonekamu', text: 'Jelajahi butik 3D atau lihat semua produk, lalu pilih warnanya.' },
    { icon: ShoppingBag, title: 'Tambah ke Keranjang', text: 'Kumpulkan sebanyak yang kamu mau dan tambahkan catatan untuk kami.' },
    { icon: MessageCircleHeart, title: 'Kirim lewat WhatsApp', text: 'Isi keranjang berubah menjadi pesan siap kirim, jadi memesan hanya butuh hitungan detik.' },
    { icon: PackageCheck, title: 'Kami konfirmasi dan kirim', text: 'Kami balas untuk konfirmasi stok, pembayaran, dan pengiriman bersamamu.' }
]

export const HowToOrderSection: FC = (): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useReveal(root)

    return (
        <section ref={root} id='how-to-order' className='scroll-mt-24 bg-[linear-gradient(180deg,#fffaf3_0%,#f8efe3_100%)] py-24 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    align='center'
                    eyebrow='Cara memesan'
                    title={<>Dari tangan kami ke tanganmu <span className='italic text-blush-600'>dalam empat langkah</span></>}
                />

                <div className='relative mt-16'>
                    <div aria-hidden='true' className='pointer-events-none absolute left-[12%] right-[12%] top-8 hidden h-px bg-[repeating-linear-gradient(90deg,#e8a5b0_0_8px,transparent_8px_16px)] lg:block' />
                    <ol className='relative grid gap-6 md:grid-cols-2 lg:grid-cols-4'>
                        {ORDER_STEPS.map((step, index) => {
                            const Icon = step.icon

                            return (
                                <li key={step.title} data-reveal className='relative flex flex-col items-center text-center'>
                                    <span className='relative grid size-16 place-items-center rounded-full bg-white text-blush-600 shadow-[0_14px_30px_-18px_rgba(58,40,30,0.6)] ring-8 ring-cream-50'>
                                        <Icon className='size-6' />
                                        <span className='absolute -right-1 -top-1 grid size-6 place-items-center rounded-full bg-cocoa-800 text-[11px] font-bold text-cream-50'>
                                            {index + 1}
                                        </span>
                                    </span>
                                    <h3 className='mt-6 font-heading text-xl text-cocoa-900'>
                                        {step.title}
                                    </h3>
                                    <p className='mt-2 max-w-[240px] text-sm leading-relaxed text-muted-foreground'>
                                        {step.text}
                                    </p>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </div>
        </section>
    )
}
